/**
 * useScrapAudience - Hook para gerenciar exportação/importação ScrapAudience
 *
 * Gerencia todo o fluxo de exportação de domínios para o ScrapAudience
 * e importação dos dados coletados de volta para o sistema.
 */
import { useState, useCallback } from 'react';
import { exportPendingLinks, listScraperFiles, importScraperFile } from '../../services/api';

export const useScrapAudience = () => {
    // Estados dos dialogs
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);

    // Estados de exportação
    const [exportFilterType, setExportFilterType] = useState('missing');
    const [exportFormat, setExportFormat] = useState('json');

    // Estados de importação
    const [availableFiles, setAvailableFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState('');

    /**
     * Abre dialog para escolher filtro de exportação
     */
    const openExportDialog = useCallback(() => {
        setExportDialogOpen(true);
    }, []);

    /**
     * Exporta domínios para JSON/Excel e baixa o arquivo
     * @param {Array} selectedIds - IDs selecionados para exportação (opcional)
     */
    const exportDomains = useCallback(async (selectedIds = null) => {
        try {
            setExportDialogOpen(false);

            // Se tiver IDs selecionados, ignora o filtro de tipo
            const filterToUse = selectedIds && selectedIds.length > 0 ? 'selected' : exportFilterType;

            const response = await exportPendingLinks(filterToUse, selectedIds, exportFormat);

            if (exportFormat === 'excel') {
                // Tratamento para Blob (Excel)
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `pending-domains-${Date.now()}.xlsx`); // Nome padrão
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                return { success: true };
            } else {
                // Tratamento JSON existente
                const data = response.data;
                if (data.success) {
                    const blob = new Blob([JSON.stringify(data.data, null, 2)], {
                        type: 'application/json'
                    });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = data.exportFile || `pending-links-${Date.now()}.json`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);

                    // Mostrar instruções apenas se for JSON (para o scraper)
                    setInstructionsDialogOpen(true);

                    return { success: true };
                }
                return { success: false, error: 'Falha ao exportar domínios' };
            }

        } catch (err) {
            console.error('Erro ao exportar:', err);
            return {
                success: false,
                error: 'Erro ao exportar domínios: ' + (err.response?.data?.message || err.message)
            };
        }
    }, [exportFilterType, exportFormat]);

    /**
     * Lista arquivos disponíveis e abre dialog de importação
     */
    const openImportDialog = useCallback(async () => {
        try {
            // Listar arquivos disponíveis
            const response = await listScraperFiles();
            const files = response.data.files || [];

            if (files.length === 0) {
                return {
                    success: false,
                    error: 'Nenhum arquivo do ScrapAudience encontrado. Execute o scraping primeiro.'
                };
            }

            setAvailableFiles(files);
            setSelectedFile(files[0]?.filename || '');
            setImportDialogOpen(true);

            return { success: true };
        } catch (err) {
            console.error('Erro ao listar arquivos:', err);
            return {
                success: false,
                error: 'Erro ao listar arquivos: ' + (err.response?.data?.message || err.message)
            };
        }
    }, []);

    /**
     * Importa arquivo selecionado do ScrapAudience
     */
    const importFile = useCallback(async () => {
        if (!selectedFile) {
            return { success: false, error: 'Selecione um arquivo para importar' };
        }

        try {
            setImportDialogOpen(false);

            const response = await importScraperFile(selectedFile);
            const data = response.data;

            if (data.success) {
                return { success: true, data };
            }

            return { success: false, error: 'Falha ao importar dados' };
        } catch (err) {
            console.error('Erro ao importar:', err);
            return {
                success: false,
                error: 'Erro ao importar dados: ' + (err.response?.data?.message || err.message)
            };
        }
    }, [selectedFile]);

    return {
        // Estados dos dialogs
        exportDialogOpen,
        importDialogOpen,
        instructionsDialogOpen,

        // Estados de exportação
        exportFilterType,
        exportFormat,

        // Estados de importação
        availableFiles,
        selectedFile,

        // Setters
        setExportDialogOpen,
        setImportDialogOpen,
        setInstructionsDialogOpen,
        setExportFilterType,
        setExportFormat,
        setSelectedFile,

        // Ações
        openExportDialog,
        exportDomains,
        openImportDialog,
        importFile
    };
};
